import { Component, OnInit, ViewChild } from '@angular/core';
import { DataSourcesService } from '../../Services/DataProcess/datasources.service';
import { LoginService } from '../../Services/login.service';
import { ProductDataSource } from '../../Model/dataProcess/productDataSource';
import { SystemService } from '../../Services/system.service';
import { Brand } from '../../Model/Dto/Brand/Brand';
import { RowRemovedEvent } from 'devextreme/ui/data_grid';
import { TranslateService } from '@ngx-translate/core';
import { OAuthService } from 'angular-oauth2-oidc';
import { DxFileUploaderTypes } from 'devextreme-angular/ui/file-uploader';
import { Address } from '../../Model/Catalog/Address';
import { DxDataGridComponent } from 'devextreme-angular';
import { JobService } from '../../Services/job.service';
import { CountryCode } from "../../Model/countryCode";

@Component({
    selector: 'app-datasources',
    templateUrl: './datasources.component.html',
    styleUrls: ['./datasources.component.css'],
    standalone: false
})
export class DatasourcesComponent implements OnInit {

  @ViewChild('dataSourceGrid', { static: false })
  dataSourceGrid!: DxDataGridComponent;

  productDataSources: ProductDataSource[] = [];
  relatedBrands: Brand[] = [];

  types = [
    { value: 'Manufacturer', text: this.translate.instant('Hersteller') },
    { value: 'Supplier',     text: this.translate.instant('Lieferant') },
  ];
  countryCodes: CountryCode[] = [];
  popupVisible: boolean = false;
  newOrUpdateProductDataSource: ProductDataSource;
  editMode: boolean = false;
  isEuCountry: boolean = false;

  EU_CODES = new Set<string>([
    'AT', 'BE', 'BG', 'HR', 'CY', 'CZ', 'DK',
    'EE', 'FI', 'FR', 'DE', 'GR', 'HU', 'IE', 'IT',
    'LV', 'LT', 'LU', 'MT', 'NL', 'PL', 'PT',
    'RO', 'SK', 'SI', 'ES', 'SE'
  ]);

  importExcelVisible: boolean;

  constructor(
    public dataSourcesService: DataSourcesService,
    public loginService: LoginService,
    public translate: TranslateService,
    public systemService: SystemService,
    public oAuthService: OAuthService,
    public jobService: JobService
    ) {
    this.systemService.currentNavigationTitle = this.translate.instant('Lieferanten');
    this.showAddOrUpdateDataSource = this.showAddOrUpdateDataSource.bind(this);
    this.startJobListener = this.startJobListener.bind(this);
    this.reloadDataSources = this.reloadDataSources.bind(this);
  }

  async ngOnInit() {
    await this.reloadDataSources();
  }

  typeDisplay = (row: any) => {
    switch (row?.type) {
      case 'Manufacturer': return this.translate.instant('Hersteller');
      case 'Supplier': return this.translate.instant('Lieferant');
      default: return row?.type ?? '';
    }
  };

  canDeactivate(): boolean {
    return true;
  }

  onToolbarPreparing(e) {

    e.toolbarOptions.items.unshift({
      location: 'before',
      locateInMenu: 'auto',
      template: 'exportDataSourceButtonTemplate'
    });
    e.toolbarOptions.items.unshift({
      location: 'before',
      locateInMenu: 'auto',
      template: 'importDataSourceButtonTemplate'
    });
    e.toolbarOptions.items.unshift({
      location: 'before',
      locateInMenu: 'auto',
      template: 'newDataSourceButtonTemplate'
    });
  }

  onCountryChanged(newCode: string): void {
    this.isEuCountry = this.EU_CODES.has(newCode);
  }

  deepClone<T>(obj: T): T {
    return typeof structuredClone === 'function'
      ? structuredClone(obj)
      : JSON.parse(JSON.stringify(obj));
  }

  async showAddOrUpdateDataSource(productDataSource?: ProductDataSource) {
    const isEdit = !!productDataSource;

    this.newOrUpdateProductDataSource = isEdit
      ? this.deepClone(productDataSource!)
      : {} as ProductDataSource;

    this.editMode = isEdit;
    this.relatedBrands = isEdit
      ? await this.dataSourcesService.getRelatedBrands(productDataSource!.id, this.loginService.currentCustomer.id)
      : [];

    if (!this.newOrUpdateProductDataSource.addressObj)
      this.newOrUpdateProductDataSource.addressObj = new Address();
    if (!this.newOrUpdateProductDataSource.importerAddress)
      this.newOrUpdateProductDataSource.importerAddress = new Address();

    this.popupVisible = true;
  }

  async onEditSaved() {
    await this.reloadDataSources();
  }

  getSmileyEmoji(data: ProductDataSource): string {
    switch (this.getSecurityInformationDegree(data)) {
      case 2: return '😊︎';
      case 1: return '😐︎';
      default: return '☹︎';
    }
  }

  getSmileyClass(data: ProductDataSource): string {
    var degree = this.getSecurityInformationDegree(data);
    if (degree == 2) { return 'smiley-green'; }
    if (degree == 1) { return 'smiley-yellow'; }
    return 'smiley-red';
  }

  isFilled(value: string | string[] | null | undefined): boolean {
    if (Array.isArray(value)) { return value.length > 0; }
    return value != null && value.toString().trim() !== '';
  }

  getSecurityInformationDegree(ds: ProductDataSource): 0 | 1 | 2 {
    const requiredOk =
      this.isFilled(ds.name) &&
      this.isFilled(ds.type) &&
      this.isFilled(ds.kreditorNr) &&
      this.isFilled(ds.addressObj?.street) &&
      this.isFilled(ds.countryCode);

    if (!requiredOk) {
      return 0;
    }

    const contactOk =
      this.isFilled(ds.contactPerson) &&
      this.isFilled(ds.supportTel) &&
      this.isFilled(ds.supportMail);

    const websiteOk = this.isFilled(ds.website);

    return contactOk && websiteOk ? 2 : 1;
  }

  displayLastUpdate = (row: any) =>
    this.isDefaultDate(row?.lastUpdate) ? null : row.lastUpdate;

  isDefaultDate(v: any) {
    return v === '0001-01-01T00:00:00Z' || (v && new Date(v).getUTCFullYear() === 1);
  }

  async onUploaded(e: DxFileUploaderTypes.UploadedEvent) {
    this.newOrUpdateProductDataSource.logoFile = e.request.responseText as string;
    this.reloadDataSources();
  }

  async onRowRemoved(event: RowRemovedEvent) {
    let key = event.data.id;
    await this.dataSourcesService.delete(key, this.loginService.currentCustomer.id);
    this.reloadDataSources();
  }

  onBeforeSendUpload(e: { request: XMLHttpRequest }) {
    e.request.setRequestHeader("customerId", this.loginService.currentUser?.customerId);
    e.request.withCredentials = true;
    e.request.setRequestHeader('Authorization', 'Bearer ' + this.oAuthService.getAccessToken());
  }

  onImportUploaded() {
    this.startJobListener();
    this.importExcelVisible = false;
    this.systemService.notifyInfo(this.translate.instant("Import Verarbeitung – Bitte haben Sie einen Moment Geduld, gerade werden Ihre Daten im Hintergrund verarbeitet."));
  }

  async startJobListener() {
    var isRunning = await this.jobService.isJobRunningThatChangesDatasourceList(this.loginService.currentCustomer.id);
    if (isRunning) {
      setTimeout(() => this.startJobListener(), 2000);
    }
    else {
      this.reloadDataSources();
    }
  }

  exportDataSource() {
    this.systemService.notifyInfo("Lieferanten Export gestartet");
    this.dataSourcesService.startExcelExport(this.loginService.currentCustomer.id);
  }

  showImportDataSource() {
    this.importExcelVisible = true;
  }

  async reloadDataSources() {
    this.productDataSources = await this.dataSourcesService.getAll(this.loginService.currentCustomer.id);
  }
}
